import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { appRoutes, Language } from '@tekman/onmat/constants';
import { Course, Group, MenuItem, ProgramEnum } from '@tekman/onmat/models';
import { EnvironmentService, UserService } from '@tekman/onmat/service/generic';
import { FeaturitWrapperService } from '@tekman/onmat/service/onmat';
import { GroupsFacade } from '@tekman/onmat/store/group';
import { ProfileFacade } from '@tekman/onmat/store/profile';
import { CoursesFacade } from '@tekman/onmat/store/courses';
import { ISelectConfig } from '@tekman/shared/component/generic';
import { StorageService } from '@tekman/shared/service';
import { combineLatest, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { FeaturitEventActivity } from '@tekman/shared/models';
import { SelectorService } from '@tekman/onmat/component/hoc';
import * as crypto from 'crypto-js';
import { environment } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { marker } from '@colsen1991/ngx-translate-extract-marker';

@Component({
  selector: 'zero-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  initials: string;
  navigation: MenuItem[];
  url: string;
  isResultsPage: boolean;
  isDemoOnmat: boolean;
  appRoutes = appRoutes;
  hasGroupsAssigned = false;
  private selectedGroup: Group;
  private groupId: string;
  private onDestroy$ = new Subject<boolean>();
  private courses: Course[] = [];

  @Input() isHelpMenu: boolean;
  public selectConfig: ISelectConfig = {
    svg: {
      isShow: false,
    },
  };

  constructor(
    private readonly profileFacade: ProfileFacade,
    public readonly userService: UserService,
    private readonly _router: Router,
    private readonly featuritService: FeaturitWrapperService,
    private readonly groupsFacade: GroupsFacade,
    private readonly storageService: StorageService,
    private selectorService: SelectorService,
    private environment: EnvironmentService,
    private translateService: TranslateService,
    private readonly coursesFacade: CoursesFacade
  ) {}

  ngOnInit(): void {
    const selectedGroup$ = this.groupsFacade.selectedGroup$.pipe(filter(selectGroup => !!selectGroup));

    // Courses are only needed for the Tekman Digital link of EMAT groups, and are requested once there is a
    // selected group: the header is created on bootstrap, before LicensesGuard has picked the API host and
    // loaded the profile, and an auth error there sends the user to error/404.
    selectedGroup$
      .pipe(
        filter(group => group.programId === ProgramEnum.EMAT),
        take(1),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => this.coursesFacade.getCourses());

    combineLatest([selectedGroup$, this.groupsFacade.groups$.pipe(filter(groups => !!groups)), this.coursesFacade.courses$])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(([selectedGroup, groups, courses]) => {
        this.hasGroupsAssigned = true;
        this.selectedGroup = selectedGroup;
        this.courses = courses;
        if (groups.length === 0) {
          this._router.navigate([appRoutes.groups]);
        } else {
          this.initMenu();
        }
      });

    this.groupsFacade.error$
      .pipe(
        takeUntil(this.onDestroy$),
        filter(error => !!error)
      )
      .subscribe(error => {
        if (error.message === 'No tiene grupo') {
          this._router.navigate([appRoutes.groups]);
        }
      });

    this.selectorService.currentGroup$.pipe(takeUntil(this.onDestroy$)).subscribe(selectedGroup => {
      this.hasGroupsAssigned = true;
      this.groupId = selectedGroup;
    });
    this.url = window.location.href;
    this.profileFacade.userInfo$.pipe(takeUntil(this.onDestroy$)).subscribe(userInfo => {
      this.isDemoOnmat = userInfo.isDemo;
    });
    this.initials = (this.userService.getUser().firstName?.charAt(0) || '') + (this.userService.getUser().lastName?.charAt(0) || '');
    this.urlListener()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(res => {
        this.url = window.location.href;
      });
  }

  options(action: 'leanwords' | 'goHelpCenter'): void {
    switch (action) {
      case 'goHelpCenter':
        window.open(appRoutes.helpCenterOnmat, '_blank');
        break;
      case 'leanwords':
        if (this.storageService.languageLicense === Language.CAT) {
          window.open(this.environment.settings.learnWordsRedirect.ca, '_blank');
        } else {
          window.open(this.environment.settings.learnWordsRedirect.es, '_blank');
        }
        break;
    }
  }

  initMenu(): void {
    this.navigation = [
      {
        id: 0,
        label: marker('f.menu.my-course'),
        link: appRoutes.itineraries,
        visible: true,
        track: this.featuritService.eventKeys.itinerariesShow,
      },
      {
        id: 1,
        label: marker('f.menu.tracking'),
        link: appRoutes.tracking,
        visible: this.userService.isTeacher(),
        track: this.featuritService.eventKeys.monitoringShow,
      },
      {
        id: 3,
        label: marker('f.menu.center'),
        link: appRoutes.groups,
        visible: this.userService.isTeacher(),
        track: this.featuritService.eventKeys.groupsShow,
        options: [
          { label: marker('f.menu.group'), icon: 'students-group-activated', action: 'group' },
          { label: marker('f.menu.teacher'), icon: 'teacher', action: 'teacher' },
          { label: marker('f.generic.report'), icon: 'graphic', action: 'report' },
        ],
      },
    ];
    if (this.selectedGroup?.programId === ProgramEnum.EMAT) {
      const tkCourseCode = this.courses.find(course => course.id === this.selectedGroup.courseId)?.tkCourseCode;
      this.navigation.push({
        id: 4,
        label: marker('f.menu.tekmanDigital'),
        link: `${environment.tekmanDigital}EMAT/${tkCourseCode}-${this.translateService.currentLang.toUpperCase()}`,
        // Hidden until the courses are loaded, so it never links to EMAT/undefined-XXX.
        visible: !!tkCourseCode,
        track: this.featuritService.eventKeys.resourcesShow,
      });
    } else {
      this.navigation.push({
        id: 2,
        label: marker('f.menu.materials'),
        link: appRoutes.resources,
        visible: true,
        track: this.featuritService.eventKeys.resourcesShow,
      });
    }

    this.navigation.sort((a, b) => a.id - b.id);
  }

  goTo(item: MenuItem): void {
    this.featuritService.trackHeaderNavigation(item.track);
    if (item.id === 4) {
      window.location.href = item.link;
    } else if (item.id === 0 || item.id === 1 || item.id === 2) {
      this._router.navigate([item.link, this.groupId]).then(_ => (this.url = window.location.href));
    } else {
      this._router.navigate([item.link]).then(_ => (this.url = window.location.href));
    }
  }

  optionsMenu(action: 'report' | 'group' | 'teacher'): void {
    switch (action) {
      case 'group':
        this._router.navigate([appRoutes.groups]);
        break;
      case 'teacher':
        this._router.navigate([appRoutes.teachers]);
        break;
      case 'report':
        this.openReport();
        break;
    }
  }

  navigateHome(): void {
    if (this.selectedGroup.progress.stepId) {
      this._router.navigate([appRoutes.itineraries, this.groupId, appRoutes.steps, this.selectedGroup.progress.stepId]);
    } else {
      this._router.navigate([appRoutes.itineraries, this.groupId]);
    }
  }

  urlListener() {
    return this._router.events.pipe(
      filter((route): route is NavigationEnd => route instanceof NavigationEnd),
      map(route => {
        this.isResultsPage = route.url.includes(appRoutes.results);
        return (this.url =
          (route.url.includes(appRoutes.itineraries) && route.url.includes(appRoutes.steps)) || route.url.includes(appRoutes.activities)
            ? ''
            : this.url);
      })
    );
  }

  async logout(): Promise<void> {
    this.userService.logout();
    document.getElementsByTagName('html')[0].classList.add('cursor-wait');
    document.getElementsByClassName('dropdown-item')[0].classList.add('cursor-wait');
    this.featuritService.track(this.featuritService.eventKeys.logout);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  private trackEvent(label: any, path: any, object: any, eventKey: any): void {
    const trackingProps: FeaturitEventActivity<string> = new FeaturitEventActivity({
      ui_label: label,
      ui_path: path,
      ui_object: object,
    });
    this.featuritService.track(eventKey, trackingProps);
  }

  public openReport(): void {
    this.profileFacade.profile$.pipe(takeUntil(this.onDestroy$)).subscribe(profile => {
      const lang = profile.locale;
      const schoolId = crypto.SHA256(profile.schoolId.toString()).toString(crypto.enc.Hex);
      const selectedGroupId = this.groupId;
      const urlReportEsp = `${appRoutes.spaceXReportESP}params=%7B%22ds30.school_id_param%22:%22${schoolId}%22,%22ds30.course_param%22:%22${selectedGroupId}%22%7D`;
      const urlReportCat = `${appRoutes.spaceXReportCAT}params=%7B%22ds13.school_id_param%22:%22${schoolId}%22,%22ds13.course_param%22:%22${selectedGroupId}%22%7D`;
      const urlReportMex = `${appRoutes.spaceXReportMEX}params=%7B%22ds13.school_id_param%22:%22${schoolId}%22,%22ds13.course_param%22:%22${selectedGroupId}%22%7D`;
      const urlReportEcu = `${appRoutes.spaceXReportECU}params=%7B%22ds14.school_id_param%22:%22${schoolId}%22,%22ds14.course_param%22:%22${selectedGroupId}%22%7D`;
      switch (lang) {
        case Language.ESP:
          window.open(urlReportEsp, '_blank');
          break;
        case Language.CAT:
          window.open(urlReportCat, '_blank');
          break;
        case Language.MEX:
          window.open(urlReportMex, '_blank');
          break;
        case Language.ECU:
          window.open(urlReportEcu, '_blank');
          break;
        default:
          window.open(urlReportEsp, '_blank');
          break;
      }
    });
    this.trackEvent('Informe', 'group', 'button', 'report_show');
  }
}
